import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Globe, 
  BarChart3,
  Clock,
  TrendingUp,
  RefreshCw,
  Loader2,
  Database,
  Zap,
  Activity,
  FileText,
  Target,
  Calendar,
  Upload,
  Download,
  CheckCircle,
  X,
  AlertCircle
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { user, isAdmin } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [cacheDetails, setCacheDetails] = useState(null);
  const [scoringSummary, setScoringSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [autoScore, setAutoScore] = useState(true);
  const [isScoringAll, setIsScoringAll] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setRefreshing(true);
    try {
      const [analyticsRes, cacheRes, scoringRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/analytics/cache`),
        axios.get(`${API}/scores/summary`)
      ]);
      setAnalytics(analyticsRes.data);
      setCacheDetails(cacheRes.data);
      setScoringSummary(scoringRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.csv', '.tmx'];
      const isValid = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!isValid) {
        toast.error("Please select a CSV or TMX file");
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error("Please login as admin to upload references");
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Determine endpoint based on file type
      const isTmx = selectedFile.name.endsWith('.tmx');
      let endpoint = `${API}/references/upload?auto_score=${autoScore}`;
      
      if (isTmx) {
        // Determine dataset type from filename
        const datasetType = selectedFile.name.toLowerCase().includes('val') ? 'validation' : 'training';
        endpoint = `${API}/references/upload-tmx?dataset_type=${datasetType}&auto_score=${autoScore}`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setUploadResult(response.data);
      const count = response.data.references_added || response.data.total_references;
      toast.success(`Uploaded ${count} references from ${isTmx ? 'TMX' : 'CSV'}`);
      
      // Refresh analytics
      fetchAllData();
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.response?.data?.detail || "Upload failed";
      toast.error(errorMsg);
      setUploadResult({ error: errorMsg });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/references/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'reference_translations_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleScoreAll = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error("Please login as admin");
      return;
    }

    setIsScoringAll(true);
    try {
      const response = await axios.post(`${API}/references/score-all`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`Scored ${response.data.scored} translations`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Scoring failed");
    } finally {
      setIsScoringAll(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "text-primary", trend }) => (
    <Card className="bg-white">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`w-5 h-5 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        {trend && (
          <p className={`text-xs mt-1 ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week
          </p>
        )}
      </CardContent>
    </Card>
  );

  const ScoreGauge = ({ label, score, description }) => {
    const percentage = score ? score * 100 : 0;
    const getColor = (s) => {
      if (!s) return "bg-gray-200";
      if (s >= 0.8) return "bg-emerald-500";
      if (s >= 0.6) return "bg-primary";
      if (s >= 0.4) return "bg-yellow-500";
      return "bg-red-500";
    };

    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            {label}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  strokeWidth="6"
                  className="stroke-gray-100"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  strokeWidth="6"
                  strokeDasharray={`${percentage * 2.26} 226`}
                  className={`${score ? (score >= 0.8 ? 'stroke-emerald-500' : score >= 0.6 ? 'stroke-primary' : 'stroke-yellow-500') : 'stroke-gray-300'}`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold">
                  {score ? `${(score * 100).toFixed(1)}%` : 'N/A'}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted-foreground mb-1">Score Range</div>
              <div className="flex gap-1">
                {[0.2, 0.4, 0.6, 0.8, 1.0].map((threshold, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded ${score && score >= threshold - 0.2 ? getColor(threshold) : 'bg-gray-100'}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>0</span>
                <span>100</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen pb-12 bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium">Translate</Link>
            <Link to="/terminology" className="nav-link text-sm font-medium">Terminology</Link>
            <Link to="/metrics" className="nav-link text-sm font-medium">Metrics</Link>
            <Link to="/analytics" className="nav-link active text-sm font-medium">Analytics</Link>
            <Link to="/history" className="nav-link text-sm font-medium">History</Link>
            <Link to="/settings" className="nav-link text-sm font-medium">Settings</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2" data-testid="page-title">
              Analytics Dashboard
            </h1>
            <p className="text-muted-foreground">
              Cache performance, translation metrics, and BLEU/COMET quality scores
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-border" data-testid="upload-refs-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload References
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Upload Reference Translations</DialogTitle>
                    <DialogDescription>
                      Upload CSV or TMX files for BLEU/COMET scoring. Supports ECCC train.tmx and val.tmx files.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    {/* Supported formats info */}
                    <div className="p-3 bg-blue-50 rounded-lg text-sm">
                      <p className="font-medium text-blue-700 mb-1">Supported Formats:</p>
                      <ul className="text-blue-600 text-xs space-y-1">
                        <li>• <strong>TMX</strong> - Translation Memory eXchange (train.tmx, val.tmx)</li>
                        <li>• <strong>CSV</strong> - Comma-separated with headers</li>
                      </ul>
                    </div>
                    
                    {/* Download Template */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">Need a CSV template?</p>
                        <p className="text-xs text-muted-foreground">Download sample format</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                        <Download className="w-4 h-4 mr-2" />
                        Template
                      </Button>
                    </div>
                    
                    {/* File Upload */}
                    <div 
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        selectedFile ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv,.tmx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {selectedFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-5 h-5 text-primary" />
                          <span className="font-medium">{selectedFile.name}</span>
                          {selectedFile.name.endsWith('.tmx') && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">TMX</span>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Click to select CSV or TMX file
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            train.tmx → Training set | val.tmx → Validation set
                          </p>
                        </>
                      )}
                    </div>
                    
                    {/* Auto-score option */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoScore}
                        onChange={(e) => setAutoScore(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-sm">Auto-score existing translations against new references</span>
                    </label>
                    
                    {/* Upload Result */}
                    {uploadResult && (
                      <div className={`p-3 rounded-lg ${uploadResult.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {uploadResult.error ? (
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{uploadResult.error}</span>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">{uploadResult.message}</span>
                            </div>
                            {uploadResult.auto_scored > 0 && (
                              <p className="text-xs">Auto-scored {uploadResult.auto_scored} existing translations</p>
                            )}
                            {uploadResult.error_count > 0 && (
                              <p className="text-xs text-amber-600">{uploadResult.error_count} rows had errors</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <Button 
                      onClick={handleUpload} 
                      disabled={!selectedFile || isUploading}
                      className="w-full"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload References
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <Button
              variant="outline"
              onClick={fetchAllData}
              disabled={refreshing}
              className="border-border"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="bg-white">
                <CardHeader className="pb-2">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : analytics ? (
          <>
            {/* Cache Performance Section */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Cache Performance
              </h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Cache Hit Rate"
                  value={`${analytics.cache_hit_rate.toFixed(1)}%`}
                  subtitle="Requests served from cache"
                  icon={Database}
                  color="text-emerald-500"
                />
                <StatCard
                  title="Cached Responses"
                  value={`${analytics.avg_cached_response_ms.toFixed(0)}ms`}
                  subtitle="Average response time"
                  icon={Zap}
                  color="text-yellow-500"
                />
                <StatCard
                  title="Non-Cached Responses"
                  value={`${analytics.avg_non_cached_response_ms.toFixed(0)}ms`}
                  subtitle="Average response time"
                  icon={Clock}
                  color="text-blue-500"
                />
                <StatCard
                  title="Cache Size"
                  value={analytics.cache_size}
                  subtitle={`${analytics.total_cache_hits} total hits`}
                  icon={Database}
                  color="text-purple-500"
                />
              </div>
              
              {/* Speed Improvement Callout */}
              {analytics.avg_non_cached_response_ms > 0 && (
                <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <TrendingUp className="w-5 h-5" />
                    <span className="font-semibold">
                      {((1 - analytics.avg_cached_response_ms / analytics.avg_non_cached_response_ms) * 100).toFixed(1)}% faster
                    </span>
                    <span className="text-emerald-600">
                      with caching enabled ({analytics.avg_non_cached_response_ms.toFixed(0)}ms → {analytics.avg_cached_response_ms.toFixed(0)}ms)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Quality Scores Section */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Quality Scores (BLEU/COMET)
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                <ScoreGauge
                  label="BLEU Score"
                  score={scoringSummary?.avg_bleu_score}
                  description="Bilingual Evaluation Understudy - measures n-gram precision against reference translations"
                />
                <ScoreGauge
                  label="COMET Score"
                  score={scoringSummary?.avg_comet_score}
                  description="Crosslingual Optimized Metric for Evaluation - neural-based quality estimation"
                />
              </div>
              
              {scoringSummary?.total_scored > 0 ? (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-700">
                      <FileText className="w-5 h-5" />
                      <span>
                        Based on <strong>{scoringSummary.total_scored}</strong> scored translations against reference corpus.
                        {scoringSummary.bleu_range && (
                          <span className="ml-2 text-blue-600">
                            BLEU range: {(scoringSummary.bleu_range.min * 100).toFixed(1)}% - {(scoringSummary.bleu_range.max * 100).toFixed(1)}%
                          </span>
                        )}
                      </span>
                    </div>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleScoreAll}
                        disabled={isScoringAll}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      >
                        {isScoringAll ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Scoring...
                          </>
                        ) : (
                          <>
                            <Target className="w-4 h-4 mr-2" />
                            Score All
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText className="w-5 h-5" />
                      <span>
                        No translations have been scored yet. Upload reference translations to enable BLEU/COMET scoring.
                      </span>
                    </div>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setUploadDialogOpen(true)}
                        className="border-gray-300"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload References
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Response Time Percentiles */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-500" />
                Response Time Distribution
              </h2>
              <Card className="bg-white">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary">{analytics.response_time_p50}ms</div>
                      <div className="text-sm text-muted-foreground">P50 (Median)</div>
                      <div className="text-xs text-emerald-600 mt-1">50% of requests faster</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-600">{analytics.response_time_p95}ms</div>
                      <div className="text-sm text-muted-foreground">P95</div>
                      <div className="text-xs text-yellow-600 mt-1">95% of requests faster</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-500">{analytics.response_time_p99}ms</div>
                      <div className="text-sm text-muted-foreground">P99</div>
                      <div className="text-xs text-red-500 mt-1">99% of requests faster</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Translation Volume */}
            <div className="grid gap-6 md:grid-cols-2 mb-8">
              {/* Volume Stats */}
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Translation Volume
                </h2>
                <div className="grid gap-4">
                  <StatCard
                    title="Today"
                    value={analytics.translations_today}
                    icon={Calendar}
                    color="text-emerald-500"
                  />
                  <StatCard
                    title="This Week"
                    value={analytics.translations_this_week}
                    icon={Calendar}
                    color="text-blue-500"
                  />
                  <StatCard
                    title="This Month"
                    value={analytics.translations_this_month}
                    icon={Calendar}
                    color="text-purple-500"
                  />
                </div>
              </div>

              {/* Daily Volume Chart */}
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Daily Volume (Last 7 Days)
                </h2>
                <Card className="bg-white h-[calc(100%-2rem)]">
                  <CardContent className="pt-6 h-full">
                    <div className="flex items-end justify-between h-48 gap-2">
                      {analytics.daily_volume.map((day, i) => {
                        const maxCount = Math.max(...analytics.daily_volume.map(d => d.count), 1);
                        const height = (day.count / maxCount) * 100;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-2">
                            <span className="text-xs font-medium">{day.count}</span>
                            <div 
                              className="w-full bg-primary rounded-t transition-all duration-300"
                              style={{ height: `${Math.max(height, 4)}%` }}
                            />
                            <span className="text-xs text-muted-foreground">{day.day}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Top Phrases */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Most Frequently Translated Phrases
              </h2>
              <Card className="bg-white">
                <CardContent className="pt-6">
                  {analytics.top_phrases.length > 0 ? (
                    <div className="space-y-4">
                      {analytics.top_phrases.slice(0, 5).map((phrase, i) => (
                        <div key={i} className="flex items-center gap-4">
                          <span className="text-lg font-bold text-muted-foreground w-6">#{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{phrase.text}</p>
                            <Progress 
                              value={(phrase.count / analytics.top_phrases[0].count) * 100} 
                              className="h-1 mt-1"
                            />
                          </div>
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {phrase.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No translation data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Cache Details */}
            {cacheDetails && (
              <div className="mb-8">
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-purple-500" />
                  Cache Details
                </h2>
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-medium mb-3">Top Cached Translations</h3>
                        <div className="space-y-2">
                          {cacheDetails.top_cached_translations?.slice(0, 5).map((item, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="truncate max-w-[200px]">{item.text}</span>
                              <span className="text-muted-foreground">{item.hits} hits</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium mb-3">Cache Age Distribution</h3>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Recent (&lt;12h)</span>
                              <span>{cacheDetails.cache_age?.recent_12h || 0}</span>
                            </div>
                            <Progress 
                              value={((cacheDetails.cache_age?.recent_12h || 0) / (cacheDetails.total_cached || 1)) * 100}
                              className="h-2"
                            />
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Older (&gt;12h)</span>
                              <span>{cacheDetails.cache_age?.older_12h || 0}</span>
                            </div>
                            <Progress 
                              value={((cacheDetails.cache_age?.older_12h || 0) / (cacheDetails.total_cached || 1)) * 100}
                              className="h-2 [&>div]:bg-yellow-500"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-4">
                          Cache expires after {cacheDetails.cache_expiry_hours} hours
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* RFI Compliance */}
            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h3 className="font-heading text-lg font-bold mb-2 text-emerald-700">
                ECCC RFI Compliance Notes
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong>BLEU/COMET Scoring:</strong> Actual scores calculated against reference translations. Add more references via API for comprehensive scoring.</li>
                <li>• <strong>Performance Target:</strong> &lt;500ms for real-time 24/7 operations. Cached responses achieve ~1ms.</li>
                <li>• <strong>Caching:</strong> Reduces API costs and improves response times by {((1 - analytics.avg_cached_response_ms / analytics.avg_non_cached_response_ms) * 100).toFixed(0)}%</li>
                <li>• <strong>Audit Trail:</strong> All translations logged with timestamps, confidence scores, and model information</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Failed to load analytics. Please try again.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
